import { Plus, Trash2 } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EVENT_REMINDER_DAY_MAX_VALUE,
  EVENT_REMINDER_HOUR_MAX_VALUE,
  EVENT_REMINDER_MINUTE_MAX_VALUE,
  EVENT_REMINDER_WEEK_MAX_VALUE,
  UNIT_DAY,
  UNIT_HOUR,
  UNIT_MINUTE,
  UNIT_WEEK,
} from "@/constants";

export interface ReminderItem {
  unit: number;
  value: number;
}

interface EventReminderSettingProps {
  reminders: ReminderItem[];
  onChange: (reminders: ReminderItem[]) => void;
  maxReminders?: number;
  className?: string;
}

export const EventReminderSetting: React.FC<EventReminderSettingProps> = ({
  reminders,
  onChange,
  maxReminders = 2,
  className = "",
}) => {
  const { t } = useTranslation();
  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const reminderKeys = useMemo(
    () => reminders.map((reminder) => `${reminder.unit}-${reminder.value}`),
    [reminders],
  );

  const hasDuplicateCombination = (items: ReminderItem[]): boolean => {
    const seen = new Set<string>();
    return items.some((item) => {
      const key = `${item.unit}-${item.value}`;
      if (seen.has(key)) {
        return true;
      }
      seen.add(key);
      return false;
    });
  };

  const updateRemindersIfUnique = (updatedReminders: ReminderItem[]) => {
    if (hasDuplicateCombination(updatedReminders)) {
      setDuplicateError(t("eventCreate.reminderDuplicateError"));
      return;
    }

    setDuplicateError(null);
    onChange(updatedReminders);
  };

  const getNextAvailableReminder = (): ReminderItem | null => {
    const existingKeys = new Set(reminderKeys);
    const candidateUnits = [UNIT_DAY, UNIT_WEEK, UNIT_HOUR, UNIT_MINUTE];

    for (const unit of candidateUnits) {
      const step = unit === UNIT_MINUTE ? 10 : 1;
      const min = getMinValueForUnit(unit);
      const max = getMaxValueForUnit(unit);

      for (let value = min; value <= max; value += step) {
        const key = `${unit}-${value}`;
        if (!existingKeys.has(key)) {
          return { unit, value };
        }
      }
    }

    return null;
  };

  // Get max value based on unit type
  const getMaxValueForUnit = (unit: number): number => {
    switch (unit) {
      case UNIT_MINUTE:
        return EVENT_REMINDER_MINUTE_MAX_VALUE;
      case UNIT_HOUR:
        return EVENT_REMINDER_HOUR_MAX_VALUE;
      case UNIT_DAY:
        return EVENT_REMINDER_DAY_MAX_VALUE;
      case UNIT_WEEK:
        return EVENT_REMINDER_WEEK_MAX_VALUE;
      default:
        return EVENT_REMINDER_DAY_MAX_VALUE;
    }
  };

  const getMinValueForUnit = (unit: number): number => {
    if (unit === UNIT_MINUTE) {
      return 10;
    }
    return 1;
  };

  const sanitizeValueForUnit = (unit: number, rawValue: number): number => {
    const minValue = getMinValueForUnit(unit);
    const maxValue = getMaxValueForUnit(unit);
    const fallbackValue = Number.isFinite(rawValue) ? rawValue : minValue;
    const clampedValue = Math.min(Math.max(fallbackValue, minValue), maxValue);

    if (unit === UNIT_MINUTE) {
      const roundedValue = Math.round(clampedValue / 10) * 10;
      return roundedValue;
    }

    return clampedValue;
  };

  const handleValueChange = (index: number, value: number) => {
    const newReminders = [...reminders];
    const unit = newReminders[index].unit;
    const sanitizedValue = sanitizeValueForUnit(unit, value);

    newReminders[index] = {
      ...newReminders[index],
      value: sanitizedValue,
    };

    updateRemindersIfUnique(newReminders);
  };

  const handleUnitChange = (index: number, unit: number) => {
    const newReminders = [...reminders];
    const currentValue = newReminders[index].value;

    newReminders[index] = {
      ...newReminders[index],
      unit,
      // Ensure the value is valid for the new unit type
      value: sanitizeValueForUnit(unit, currentValue),
    };

    updateRemindersIfUnique(newReminders);
  };

  const handleRemoveReminder = (index: number) => {
    const newReminders = reminders.filter((_, i) => i !== index);
    updateRemindersIfUnique(newReminders);
  };

  const handleAddReminder = () => {
    if (reminders.length >= maxReminders) {
      return;
    }

    const nextReminder = getNextAvailableReminder();
    if (nextReminder) {
      updateRemindersIfUnique([...reminders, nextReminder]);
    } else {
      setDuplicateError(t("eventCreate.reminderDuplicateError"));
    }
  };

  const formatUnitLabel = (unit: number, value: number) => {
    if (unit === UNIT_MINUTE) return t("common.minute", { count: value });
    if (unit === UNIT_HOUR) return t("common.hour", { count: value });
    if (unit === UNIT_DAY) return t("common.day", { count: value });
    if (unit === UNIT_WEEK) return t("common.week", { count: value });
    return "";
  };

  return (
    <div className={`col-span-2 ${className}`}>
      <div className="mb-2 flex items-center justify-between">
        <Label htmlFor="reminder">{t("eventCreate.reminderTitle")}</Label>
        <span className="text-sm text-gray-500">
          {t("eventManage.reminderCount", {
            current: reminders.length,
            max: maxReminders,
          })}
        </span>
      </div>
      <div className="space-y-3">
        {reminders.map((reminder, index) => (
          <div
            key={`${index}-${reminder.unit}-${reminder.value}`}
            className="flex items-center space-x-2"
          >
            <Input
              type="number"
              min={getMinValueForUnit(reminder.unit)}
              max={getMaxValueForUnit(reminder.unit)}
              step={reminder.unit === UNIT_MINUTE ? 10 : 1}
              value={reminder.value}
              onChange={(e) =>
                handleValueChange(index, Number.parseInt(e.target.value, 10))
              }
              className="w-20"
            />
            <Select
              value={reminder.unit.toString()}
              onValueChange={(value) =>
                handleUnitChange(index, Number.parseInt(value, 10))
              }
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNIT_MINUTE.toString()}>
                  {formatUnitLabel(UNIT_MINUTE, reminder.value)}
                </SelectItem>
                <SelectItem value={UNIT_HOUR.toString()}>
                  {formatUnitLabel(UNIT_HOUR, reminder.value)}
                </SelectItem>
                <SelectItem value={UNIT_DAY.toString()}>
                  {formatUnitLabel(UNIT_DAY, reminder.value)}
                </SelectItem>
                <SelectItem value={UNIT_WEEK.toString()}>
                  {formatUnitLabel(UNIT_WEEK, reminder.value)}
                </SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-600">{t("common.before")}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveReminder(index)}
              className="text-red-600 hover:bg-red-50 hover:text-red-800"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
        {reminders.length < maxReminders && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddReminder}
            className="flex items-center text-sm"
          >
            <Plus className="mr-1 size-4" />
            {t("eventCreate.addReminder")}
          </Button>
        )}
        {duplicateError && (
          <p className="text-sm text-red-600" role="alert">
            {duplicateError}
          </p>
        )}
      </div>
    </div>
  );
};
